import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { fileUploadService, UploadedFile, UploadProgress } from '../services/fileUploadService';

interface FileUploadProps {
  value?: string; // Current file URL or key
  onChange: (file: UploadedFile | null) => void;
  folder?: string;
  accept?: string;
  maxSize?: number; // in MB
  label?: string;
  hint?: string;
  disabled?: boolean;
  className?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  value,
  onChange,
  folder = 'documents',
  accept = 'image/*,.pdf',
  maxSize = 10,
  label,
  hint,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFileSelect = useCallback(async (file: File) => {
    // Validate file type
    if (!fileUploadService.isAllowedFileType(file)) {
      toast.error(t('fileUpload.invalidFileType'));
      return;
    }

    // Validate file size
    const maxSizeBytes = maxSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      toast.error(t('fileUpload.fileTooLarge', { maxSize }));
      return;
    }

    setUploading(true);
    setProgress({ loaded: 0, total: file.size, percentage: 0 });

    try {
      const uploadedFile = await fileUploadService.uploadPassport(file, (prog) => {
        setProgress(prog);
      });

      onChange(uploadedFile);

      // Set preview for images
      if (file.type.startsWith('image/')) {
        setPreviewUrl(uploadedFile.url);
      }

      toast.success(t('fileUpload.uploadSuccess'));
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t('fileUpload.uploadFailed'));
    } finally {
      setUploading(false);
      setProgress(null);
    }
  }, [folder, maxSize, onChange, t]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);

    if (disabled || uploading) return;

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [disabled, uploading, handleFileSelect]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled && !uploading) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleRemove = async () => {
    if (value) {
      try {
        // Extract key from URL if it's a full URL
        const key = value.includes('amazonaws.com')
          ? value.split('.amazonaws.com/')[1]
          : value;

        await fileUploadService.deleteFile(key);
        onChange(null);
        setPreviewUrl(null);
        toast.success(t('fileUpload.deleteSuccess'));
      } catch (error) {
        console.error('Delete error:', error);
        toast.error(t('fileUpload.deleteFailed'));
      }
    }
  };

  const handleClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  const isImage = value?.match(/\.(jpg|jpeg|png|gif|webp)$/i) || previewUrl;
  const isPdf = value?.match(/\.pdf$/i);

  const handleViewFile = async () => {
    if (!value) return;

    try {
      // Extract key from URL if it's a full URL
      const key = value.includes('amazonaws.com')
        ? value.split('.amazonaws.com/')[1]
        : value;

      const signedUrl = await fileUploadService.getSignedUrl(key);
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Error getting signed URL:', error);
      toast.error(t('fileUpload.viewFailed'));
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled || uploading}
      />

      {!value ? (
        // Upload area
        <div
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
            transition-colors duration-200
            ${dragOver
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            ${uploading ? 'pointer-events-none' : ''}
          `}
        >
          {uploading ? (
            <div className="space-y-2">
              <div className="w-12 h-12 mx-auto">
                <svg className="animate-spin h-12 w-12 text-blue-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('fileUpload.uploading')} {progress?.percentage}%
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress?.percentage || 0}%` }}
                ></div>
              </div>
            </div>
          ) : (
            <>
              <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('fileUpload.dropOrClick')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                {t('fileUpload.allowedTypes')}
              </p>
            </>
          )}
        </div>
      ) : (
        // File preview
        <div className="relative border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center space-x-4">
            {isImage ? (
              <img
                src={previewUrl || value}
                alt="Preview"
                className="w-16 h-16 object-cover rounded-lg"
              />
            ) : isPdf ? (
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.5,11C8,11 8,14 10.5,14C10.5,14 8,14 8,16.5C8,19 10.5,19 10.5,19H12V18H10.5C10.5,18 9,18 9,16.5C9,15 10.5,15 10.5,15H12V14H10.5C8.5,14 9,12 10.5,12H12V11H10.5M14,11C13.5,11 13,11.5 13,12V18C13,18.5 13.5,19 14,19H15.5C16.5,19 17,18.5 17,17.5V12.5C17,11.5 16.5,11 15.5,11H14M14,12H15.5C15.5,12 16,12 16,12.5V17.5C16,18 15.5,18 15.5,18H14V12Z" />
                </svg>
              </div>
            ) : (
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            )}

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {t('fileUpload.fileUploaded')}
              </p>
              <button
                type="button"
                onClick={handleViewFile}
                className="text-xs text-blue-600 hover:underline"
              >
                {t('fileUpload.viewFile')}
              </button>
            </div>

            <button
              type="button"
              onClick={handleRemove}
              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              title={t('common.delete')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {hint && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      )}
    </div>
  );
};

export default FileUpload;
